import React, { useEffect, useMemo, useState } from "react";
import {
  CompanyProfile,
  Firm,
  Order,
  SendDocumentLine,
  SendDocumentResult,
} from "../types";
import {
  companyProfileService,
  eDocumentService,
  firmService,
  orderService,
} from "../services/dataService";
import { useSettings } from "../context/SettingsContext";
import { formatCurrency } from "../utils/formatters";
import { newUuid } from "../utils/uuid";
import FirmPickerField from "./FirmPicker";
import "./SendDocumentModal.css";

/**
 * Fatura oluşturma — belge gerçek bir faturanın düzeninde kurulur:
 * satıcı / alıcı blokları, belge künyesi, kalem tablosu, KDV kırılımı.
 *
 * Kurallar değişmedi: satırlar siparişten önerilerle başlar ve tamamen
 * düzenlenebilir; KDV oranı ayarlardaki listeden SEÇİLİR; ekrandaki toplamlar
 * yalnızca bilgilendirmedir — sunucuya tutar gitmez, miktar ve birim fiyat
 * gider, kesin hesap servislerin kuruş aritmetiğinde yapılır. Kesim geri
 * alınamadığı için gönderimden önce bir onay adımı vardır.
 *
 * `order` verilmezse belge SİPARİŞ DIŞI kesilir: alıcı firma ve para birimi
 * pencerede seçilir. Taslak kimliği pencere açılırken üretilir ve yeniden
 * denemelerde aynı kalır — ağ hatasından sonra tekrar basmak ikinci fatura kesmez.
 */

const CURRENCIES = ["TRY", "USD", "EUR", "GBP"];

const UNIT_OPTIONS: { id: number; name: string }[] = [
  { id: 1, name: "Adet" },
  { id: 2, name: "Metre" },
  { id: 3, name: "Takım" },
  { id: 7, name: "Pano" },
];

interface Props {
  /** Verilmezse sipariş dışı (serbest) belge. */
  order?: Order;
  onClose: () => void;
  onSent: (result: SendDocumentResult, appliedVatRate: number | null) => void;
}

interface EditableLine {
  name: string;
  description: string;
  quantity: string;
  orderUnitId: number;
  unitPrice: string;
  vatRate: number;
}

const parseNumber = (value: string): number => {
  const n = parseFloat(value.replace(",", "."));
  return isNaN(n) ? 0 : n;
};

const fmtDate = (value?: string) =>
  value ? new Date(value).toLocaleDateString("tr-TR") : "—";

const joinParts = (...parts: (string | undefined | null)[]) =>
  parts.map((p) => (p || "").trim()).filter(Boolean).join(", ");

const SendDocumentModal: React.FC<Props> = ({ order, onClose, onSent }) => {
  const { settings } = useSettings();

  // Ayarlardaki "0,1,10,20" listesi. Bozuksa yaygın oranlara düşülür ki ekran
  // kilitlenmesin; sunucu yine de kendi listesiyle doğrular.
  const vatOptions = useMemo(() => {
    const parsed = (settings?.vatRates || "")
      .split(",")
      .map((p) => parseFloat(p.trim().replace(",", ".")))
      .filter((n) => !isNaN(n) && n >= 0 && n <= 100);
    return parsed.length > 0 ? parsed : [0, 1, 10, 20];
  }, [settings?.vatRates]);

  const isStandalone = !order;

  const defaultRate = useMemo(() => {
    const suggested = order?.vatRate ?? settings?.defaultVatRate ?? 20;
    return vatOptions.includes(suggested) ? suggested : vatOptions[0];
  }, [order?.vatRate, settings?.defaultVatRate, vatOptions]);

  const includeVat = !!settings?.pricesIncludeVat;

  // Sipariş dışı belge: taslak kimliği bir kez üretilir, para birimi seçilir.
  const [requestId] = useState(newUuid);
  const [standaloneCurrency, setStandaloneCurrency] = useState("TRY");
  const currency = order ? order.priceCurrency || order.currency || "TRY" : standaloneCurrency;

  const [lines, setLines] = useState<EditableLine[]>(() => [
    order
      ? {
          // Model adı kodla aynı olduğundan "kod - ad" ikilemesi yerine: kod + ayarlardaki ek ("9025 Nolu Baskı")
          name: order.model
            ? [order.model.modelCode, (settings?.invoiceLineSuffix ?? "Nolu Baskı").trim()].filter(Boolean).join(" ")
            : "",
          description: "",
          quantity: String(order.quantity ?? 1),
          orderUnitId: (order.unit as number) || 1,
          unitPrice: order.price != null ? String(order.price) : "",
          vatRate: defaultRate,
        }
      : { name: "", description: "", quantity: "1", orderUnitId: 1, unitPrice: "", vatRate: defaultRate },
  ]);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [result, setResult] = useState<SendDocumentResult | null>(null);

  // Belge başlığı için taraflar: alıcı firma (adres, VKN, vergi dairesi) ve
  // satıcı profili. Liste satırı bunları taşımaz, ayrıca çekilir. Sipariş dışı
  // belgede alıcı pencerede seçilir.
  const [buyerId, setBuyerId] = useState<string | null>(order?.firmId || null);
  const [firm, setFirm] = useState<Firm | null>(order?.firm || null);
  const [firmLoading, setFirmLoading] = useState(false);
  const [seller, setSeller] = useState<CompanyProfile | null | undefined>(undefined);

  useEffect(() => {
    let alive = true;
    companyProfileService.get().then((p) => { if (alive) setSeller(p); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    if (buyerId) {
      setFirmLoading(true);
      firmService.getFirm(buyerId)
        .then((f) => { if (alive) setFirm(f); })
        .catch(() => { /* liste satırındaki / seçicideki özetle devam */ })
        .finally(() => { if (alive) setFirmLoading(false); });
    }
    return () => { alive = false; };
  }, [buyerId]);

  // Esc kapatır (gönderim sırasında değil). Üstte firma seçici açıksa Esc onundur:
  // seçici açılır açılmaz odak henüz ona geçmemişken basılan Esc buraya düşüp
  // girilmiş kalemlerle birlikte faturayı kapatıyordu.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !sending && !document.querySelector(".fp-overlay")) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, sending]);

  const updateLine = (index: number, patch: Partial<EditableLine>) =>
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  const addLine = () =>
    setLines((prev) => [
      ...prev,
      { name: "", description: "", quantity: "1", orderUnitId: 1, unitPrice: "", vatRate: defaultRate },
    ]);

  const removeLine = (index: number) =>
    setLines((prev) => prev.filter((_, i) => i !== index));

  // BİLGİLENDİRME toplamları. Ayar "fiyatlar KDV dahil" ise matrah geriye
  // hesaplanır (dispatcher da aynısını yapar); değilse KDV üstüne eklenir.
  const calc = useMemo(() => {
    const byRate = new Map<number, { net: number; vat: number }>();
    let net = 0, vat = 0;
    const rows = lines.map((l) => {
      const amount = parseNumber(l.quantity) * parseNumber(l.unitPrice);
      const r = l.vatRate / 100;
      const lineNet = includeVat ? amount / (1 + r) : amount;
      const lineVat = includeVat ? amount - lineNet : amount * r;
      net += lineNet; vat += lineVat;
      const b = byRate.get(l.vatRate) || { net: 0, vat: 0 };
      b.net += lineNet; b.vat += lineVat; byRate.set(l.vatRate, b);
      return { net: lineNet, vat: lineVat, gross: lineNet + lineVat };
    });
    const breakdown = Array.from(byRate.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([rate, v]) => ({ rate, ...v }));
    return { rows, net, vat, gross: net + vat, breakdown };
  }, [lines, includeVat]);

  const validateLocally = (): string[] => {
    const errs: string[] = [];
    lines.forEach((l, i) => {
      const no = i + 1;
      if (!l.name.trim()) errs.push(`Satır ${no}: iş/ürün adı boş olamaz.`);
      if (parseNumber(l.quantity) <= 0) errs.push(`Satır ${no}: miktar sıfırdan büyük olmalıdır.`);
      if (parseNumber(l.unitPrice) <= 0) errs.push(`Satır ${no}: birim fiyat sıfırdan büyük olmalıdır.`);
    });
    if (lines.length === 0) errs.push("Belgede en az bir satır olmalıdır.");
    return errs;
  };

  const startConfirm = () => {
    const localErrors = validateLocally();
    if (localErrors.length > 0) { setErrors(localErrors); setConfirming(false); return; }
    setErrors([]);
    setConfirming(true);
  };

  const handleSend = async () => {
    setSending(true);
    setErrors([]);

    const payload: SendDocumentLine[] = lines.map((l) => ({
      name: l.name.trim(),
      description: l.description.trim() || undefined,
      quantity: parseNumber(l.quantity),
      orderUnitId: l.orderUnitId,
      unitPrice: parseNumber(l.unitPrice),
      vatRate: l.vatRate,
    }));

    try {
      const res = order
        ? await orderService.sendDocument(order.orderId, {
            lines: payload,
            note: note.trim() || undefined,
          })
        : await eDocumentService.sendStandalone({
            requestId,
            firmId: firm!.firmId,
            currencyCode: currency,
            lines: payload,
            note: note.trim() || undefined,
          });
      setResult(res);
      const distinct = Array.from(new Set(payload.map((l) => l.vatRate)));
      onSent(res, distinct.length === 1 ? distinct[0] : null);
    } catch (error: any) {
      const data = error?.response?.data;
      const errs: string[] = [];
      if (data?.errors && Array.isArray(data.errors)) errs.push(...data.errors);
      else if (data?.detail) errs.push(data.title ? `${data.title}: ${data.detail}` : data.detail);
      else if (data?.title) errs.push(data.title);
      else errs.push("Fatura oluşturulamadı; sunucuya ulaşılamadı.");
      setErrors(errs);
      setConfirming(false);
    } finally {
      setSending(false);
    }
  };

  // Belge türü etiketi ve tonu — siparişte liste satırındaki tip, serbest belgede seçilen firmanınki
  const invoiceTypeId = order ? order.invoiceTypeId : firm?.invoiceTypeId;
  const docType =
    isStandalone && !firm ? { label: "Alıcı seçilmedi", tone: "missing" }
    : invoiceTypeId === 1 ? { label: "e-Fatura", tone: "efatura" }
    : invoiceTypeId === 2 ? { label: "e-Arşiv Fatura", tone: "efatura" }
    : invoiceTypeId === 3 ? { label: "Proforma Fatura", tone: "proforma" }
    : { label: "Fatura tipi seçilmemiş", tone: "missing" };

  const buyerIdentity = firm?.taxNumber
    ? { label: "VKN", value: firm.taxNumber }
    : firm?.tckn ? { label: "TCKN", value: firm.tckn } : null;
  const buyerAddress = joinParts(
    firm?.address,
    firm?.district?.districtName || firm?.district?.name,
    firm?.city?.cityName || firm?.city?.name,
  );
  const sellerAddress = seller
    ? joinParts(
        joinParts(seller.streetName, seller.buildingNumber).replace(", ", " "),
        seller.district, seller.city, seller.postalCode,
      )
    : "";

  const unitName = (id: number) => UNIT_OPTIONS.find((u) => u.id === id)?.name || "Adet";

  /* ───────────────────────── Sonuç görünümü ───────────────────────── */
  if (result) {
    return (
      <div className="ic-overlay" onClick={onClose}>
        <div className="ic-panel ic-panel-sm" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
          <div className="ic-receipt">
            <div className={`ic-receipt-badge ${result.isSimulated ? "sim" : ""}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2>{result.isSimulated ? "Fatura simülasyonda oluşturuldu" : "Fatura oluşturuldu"}</h2>
            <p className="ic-receipt-sub">
              {result.serviceKind === "FATURASIZ" ? "Proforma belge" : "e-Belge"} servise iletildi
              {result.remoteState ? ` · ${result.remoteState}` : ""}
            </p>

            <dl className="ic-receipt-grid">
              <dt>Belge No</dt><dd className="mono">{result.documentNumber || "—"}</dd>
              {result.uuid && (<><dt>ETTN</dt><dd className="mono small">{result.uuid}</dd></>)}
              <dt>Alıcı</dt><dd>{firm?.firmName || order?.firm?.firmName || "—"}</dd>
              {result.grandTotal != null && (
                <><dt>Genel Toplam</dt><dd className="strong">{formatCurrency(result.grandTotal, result.currencyCode || currency)}</dd></>
              )}
            </dl>

            {result.warning && <div className="ic-alert warn">{result.warning}</div>}

            <div className="ic-receipt-actions">
              {result.viewUrl && (
                <a className="ic-btn" href={result.viewUrl} target="_blank" rel="noreferrer">
                  Belgeyi Görüntüle
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17L17 7M9 7h8v8" /></svg>
                </a>
              )}
              <button className="ic-btn primary" onClick={onClose}>Kapat</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ───────────────────────── Düzenleme görünümü ───────────────────────── */
  return (
    <div className="ic-overlay" onClick={() => !sending && onClose()}>
      <div className="ic-panel" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="ic-title">
        {/* Üst çubuk */}
        <header className="ic-bar">
          <div className="ic-bar-left">
            <h2 id="ic-title">{isStandalone ? "Sipariş Dışı Fatura" : "Fatura Oluştur"}</h2>
            <span className={`ic-chip ${docType.tone}`}>{docType.label}</span>
            <span className="ic-chip neutral">{currency}</span>
            {includeVat && <span className="ic-chip neutral" title="Ayarlar: sipariş fiyatları KDV dahil">KDV dahil fiyat</span>}
          </div>
          <button className="ic-close" onClick={onClose} aria-label="Kapat" disabled={sending}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </header>

        <div className="ic-body">
          {docType.tone === "missing" && (isStandalone && !firm ? (
            <div className="ic-alert warn">
              Faturanın kesileceği firmayı Alıcı bölümünden seçin. Belge türü (e-Fatura / e-Arşiv /
              Faturasız) firmanın kartındaki fatura tipinden belirlenir.
            </div>
          ) : (
            <div className="ic-alert warn">
              Firmanın fatura tipi seçilmemiş. Belge kesilebilmesi için Firma Yönetimi'nden
              e-Fatura / e-Arşiv / Faturasız seçimi yapılmalıdır.
            </div>
          ))}
          {errors.length > 0 && (
            <div className="ic-alert error" role="alert">
              <strong>Fatura oluşturulamadı</strong>
              <ul>{errors.map((err, i) => <li key={i}>{err}</li>)}</ul>
            </div>
          )}

          {/* ── Belge ── */}
          <section className="ic-doc">
            <div className="ic-doc-head">
              <div className="ic-party">
                <div className="ic-party-label">Satıcı</div>
                {seller === undefined ? (
                  <div className="ic-party-muted">Yükleniyor…</div>
                ) : seller ? (
                  <>
                    <div className="ic-party-name">{seller.title}</div>
                    <div className="ic-party-line">VKN {seller.taxNumber}{seller.taxOfficeName ? ` · ${seller.taxOfficeName} V.D.` : ""}</div>
                    {sellerAddress && <div className="ic-party-line">{sellerAddress}</div>}
                    {(seller.phone || seller.email) && (
                      <div className="ic-party-line">{joinParts(seller.phone, seller.email)}</div>
                    )}
                  </>
                ) : (
                  <div className="ic-party-muted">
                    Şirket profili görüntülenemiyor. Belge, sunucudaki kayıtlı satıcı bilgileriyle kesilir.
                  </div>
                )}
              </div>

              <div className="ic-meta">
                <div className="ic-meta-title">{docType.label.toUpperCase()}</div>
                <dl>
                  <dt>Belge No</dt><dd className="muted">Servis atar</dd>
                  <dt>Düzenleme Tarihi</dt><dd>{new Date().toLocaleDateString("tr-TR")}</dd>
                  {order ? (
                    <>
                      <dt>Sipariş Tarihi</dt><dd>{fmtDate(order.acceptanceDate)}</dd>
                      <dt>Sipariş No</dt><dd className="mono">{order.orderId.slice(0, 8).toUpperCase()}</dd>
                      <dt>Para Birimi</dt><dd>{currency}</dd>
                    </>
                  ) : (
                    <>
                      <dt>Kaynak</dt><dd>Sipariş dışı</dd>
                      <dt>Para Birimi</dt>
                      <dd>
                        <select
                          className="ic-in ic-currency"
                          value={standaloneCurrency}
                          aria-label="Para birimi"
                          onChange={(e) => setStandaloneCurrency(e.target.value)}
                        >
                          {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </dd>
                    </>
                  )}
                </dl>
              </div>
            </div>

            <div className="ic-party ic-buyer">
              <div className="ic-party-label">Alıcı</div>
              {isStandalone && (
                <div className="ic-buyer-pick">
                  <FirmPickerField
                    value={buyerId}
                    onChange={(f) => { setBuyerId(f?.firmId || null); setFirm(f); }}
                    placeholder="Faturanın kesileceği firmayı seçin"
                    title="Alıcı firma"
                    allowCreate={false}
                    disabled={sending}
                  />
                </div>
              )}
              {firm ? (
                <>
                  <div className="ic-party-name">{firm.firmName}</div>
                  <div className="ic-party-line">
                    {buyerIdentity ? `${buyerIdentity.label} ${buyerIdentity.value}` : <span className="ic-missing">VKN / TCKN girilmemiş</span>}
                    {firm.taxOffice?.name ? ` · ${firm.taxOffice.name} V.D.` : ""}
                  </div>
                  {firmLoading
                    ? <div className="ic-party-muted">Firma bilgileri yükleniyor…</div>
                    : buyerAddress
                      ? <div className="ic-party-line">{buyerAddress}</div>
                      : <div className="ic-party-line ic-missing">Adres girilmemiş</div>}
                  {(firm.phone || firm.email) && (
                    <div className="ic-party-line">{joinParts(firm.phone, firm.email)}</div>
                  )}
                </>
              ) : !isStandalone ? (
                <div className="ic-party-name">{order?.firm?.firmName || "—"}</div>
              ) : null}
            </div>

            {/* Kalemler */}
            <div className="ic-table-wrap">
              <table className="ic-table">
                <thead>
                  <tr>
                    <th className="c-no">#</th>
                    <th className="c-desc">Mal / Hizmet</th>
                    <th className="c-qty">Miktar</th>
                    <th className="c-unit">Birim</th>
                    <th className="c-price">Birim Fiyat</th>
                    <th className="c-vat">KDV</th>
                    <th className="c-amt">KDV Tutarı</th>
                    <th className="c-amt">Tutar</th>
                    <th className="c-act" />
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, i) => {
                    const row = calc.rows[i];
                    return (
                      <tr key={i}>
                        <td className="c-no">{i + 1}</td>
                        <td className="c-desc">
                          <input
                            className="ic-in"
                            value={line.name}
                            placeholder="Mal veya hizmet adı"
                            onChange={(e) => updateLine(i, { name: e.target.value })}
                          />
                          <input
                            className="ic-in ic-in-sub"
                            value={line.description}
                            placeholder="Açıklama (isteğe bağlı)"
                            onChange={(e) => updateLine(i, { description: e.target.value })}
                          />
                        </td>
                        <td className="c-qty">
                          <input className="ic-in num" inputMode="decimal" value={line.quantity}
                            onChange={(e) => updateLine(i, { quantity: e.target.value })} />
                        </td>
                        <td className="c-unit">
                          <select className="ic-in" value={line.orderUnitId}
                            onChange={(e) => updateLine(i, { orderUnitId: parseInt(e.target.value, 10) })}>
                            {UNIT_OPTIONS.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                          </select>
                        </td>
                        <td className="c-price">
                          <input className="ic-in num" inputMode="decimal" value={line.unitPrice} placeholder="0,00"
                            onChange={(e) => updateLine(i, { unitPrice: e.target.value })} />
                        </td>
                        <td className="c-vat">
                          <select className="ic-in" value={line.vatRate}
                            onChange={(e) => updateLine(i, { vatRate: parseFloat(e.target.value) })}>
                            {vatOptions.map((r) => <option key={r} value={r}>%{r}</option>)}
                          </select>
                        </td>
                        <td className="c-amt ic-num">{row.vat > 0 ? formatCurrency(row.vat, currency) : "—"}</td>
                        <td className="c-amt ic-num strong">{row.gross > 0 ? formatCurrency(includeVat ? row.gross : row.net, currency) : "—"}</td>
                        <td className="c-act">
                          {lines.length > 1 && (
                            <button className="ic-rm" title="Satırı kaldır" onClick={() => removeLine(i)}>
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <button className="ic-add" onClick={addLine} type="button">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
              Satır ekle
            </button>

            {/* Not + toplamlar */}
            <div className="ic-foot">
              <div className="ic-note">
                <label htmlFor="ic-note">Belge Notu</label>
                <textarea
                  id="ic-note"
                  className="ic-in ic-textarea"
                  value={note}
                  maxLength={300}
                  rows={3}
                  placeholder="Belgenin üzerinde görünecek not (isteğe bağlı)"
                  onChange={(e) => setNote(e.target.value)}
                />
                <div className="ic-note-count">{note.length}/300</div>
              </div>

              <div className="ic-totals">
                <div className="ic-total-row"><span>Ara Toplam</span><span className="ic-num">{formatCurrency(calc.net, currency)}</span></div>
                {calc.breakdown.map((b) => (
                  <div className="ic-total-row" key={b.rate}>
                    <span>KDV %{b.rate}</span>
                    <span className="ic-num">{formatCurrency(b.vat, currency)}</span>
                  </div>
                ))}
                <div className="ic-total-row grand">
                  <span>Genel Toplam</span>
                  <span className="ic-num">{formatCurrency(calc.gross, currency)}</span>
                </div>
                <div className="ic-total-hint">
                  Tutarlar bilgilendirme amaçlıdır; kesin hesap belge servisinde kuruş üzerinden yapılır.
                  {includeVat && " Birim fiyatlar KDV dahil kabul edilir, matrah geriye hesaplanır."}
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Alt aksiyon çubuğu / onay adımı */}
        <footer className={`ic-actions ${confirming ? "confirm" : ""}`}>
          {confirming ? (
            <>
              <div className="ic-confirm-text">
                <strong>Faturayı kesmek üzeresiniz.</strong>
                <span>
                  {lines.length} kalem · {formatCurrency(calc.gross, currency)} · {firm?.firmName || order?.firm?.firmName}
                  {order
                    ? " — bu işlem geri alınamaz; aynı siparişten ikinci belge kesilemez."
                    : " — bu işlem geri alınamaz."}
                </span>
              </div>
              <div className="ic-actions-btns">
                <button className="ic-btn" onClick={() => setConfirming(false)} disabled={sending}>Geri</button>
                <button className="ic-btn danger" onClick={handleSend} disabled={sending}>
                  {sending ? (<><span className="ic-spin" aria-hidden="true" />Kesiliyor…</>) : "Onayla ve Kes"}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="ic-actions-summary">
                <span>{lines.length} kalem</span>
                <span className="ic-dot" />
                <span>Genel toplam <strong>{formatCurrency(calc.gross, currency)}</strong></span>
              </div>
              <div className="ic-actions-btns">
                <button className="ic-btn" onClick={onClose}>Vazgeç</button>
                <button className="ic-btn primary" onClick={startConfirm} disabled={docType.tone === "missing"}>
                  Faturayı Oluştur
                </button>
              </div>
            </>
          )}
        </footer>
      </div>
    </div>
  );
};

export default SendDocumentModal;
